import React, { useState, useEffect } from 'react';
import api from '../../api/api';
import AdminLayout from '../../components/AdminLayuot';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
    PieChart, Pie, Cell, Legend 
} from 'recharts';

const THEME = {
    primary: '#1F2937',
    secondary: '#3B82F6',
    success: '#10B981',
    accent: '#8B5CF6',
    warning: '#F59E0B',
    danger: '#EF4444',
    border: '#E5E7EB',
    bg: '#F3F4F6'
};

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

function AdminAnalisis() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedDetail, setSelectedDetail] = useState(null);

    useEffect(() => {
        const fetchAnalysis = async () => {
            try {
                const token = localStorage.getItem('token');
                const { data } = await api.get('/pedidos/admin/business-analysis', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setData(data);
            } catch (error) {
                console.error("Error cargando análisis:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchAnalysis();
    }, []);

    if (loading) return <AdminLayout title="Análisis"><div style={{textAlign:'center', marginTop:50}}>Calculando métricas de rentabilidad...</div></AdminLayout>;
    if (!data || !data.kpis) return <AdminLayout title="Análisis">Sin datos suficientes.</AdminLayout>;

    // Filtrar clientes que necesitan atención (Ciclo de compra vencido)
    const clientesEnRiesgo = (data.topClientes || []).filter(c => c.enRiesgo);

    return (
        <AdminLayout title="📊 Inteligencia de Negocio">
            
            {/* 1. KPIs PRINCIPALES */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20, marginBottom: 30 }}>
                <KPICard title="Ventas del Mes" value={`${(data.kpis.kgMes || 0).toFixed(1)} Kg`} sub={`${data.kpis.crecimientoKg || 0}% vs mes anterior`} color={THEME.secondary} icon="⚖️" />
                <KPICard title="Ingresos Totales" value={`$${Number(data.kpis.dineroMes || 0).toLocaleString('es-MX')}`} sub={`${data.kpis.crecimientoDinero || 0}% vs mes anterior`} color={THEME.success} icon="💵" />
                <KPICard title="Producto Estrella" value={data.kpis.productoTop?.name || 'N/A'} sub={`${(data.kpis.productoTop?.kg || 0).toFixed(1)} Kg`} color={THEME.warning} icon="⭐" />
                <KPICard title="Ticket Promedio" value={`$${Number(data.kpis.ticketPromedio || 0).toLocaleString('es-MX')}`} sub="Por pedido" color={THEME.accent} icon="🎟️" />
            </div>

            {/* 2. GESTIÓN PROACTIVA: CICLO DE COMPRA */}
            <div style={{ background: 'white', padding: 25, borderRadius: 12, boxShadow: '0 4px 6px rgba(0,0,0,0.05)', marginBottom: 30, borderLeft: `6px solid ${clientesEnRiesgo.length > 0 ? THEME.danger : THEME.success}` }}>
                <h3 style={{ marginTop: 0, marginBottom: 15, color: THEME.primary }}>🔄 Gestión de Retención (Ciclo de Compra)</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
                    {clientesEnRiesgo.length > 0 ? (
                        clientesEnRiesgo.map((c, i) => (
                            <div key={i} style={{ border: `1px solid ${THEME.danger}40`, borderRadius: 10, padding: 15, background: '#FEF2F2', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <strong style={{ color: THEME.danger, fontSize: 14 }}>🚨 {c.nombre}</strong>
                                    <div style={{ fontSize: 11, color: '#4B5563', marginTop: 4 }}>
                                        Ciclo: cada <b>{c.frecuencia} días</b>.<br />
                                        Inactivo hace: <b style={{color: THEME.danger}}>{c.diasDesdeUltimo} días</b>.
                                    </div>
                                </div>
                                <button 
                                    onClick={() => window.open(`https://wa.me/?text=Hola%20${c.nombre},%20te%20escribimos%20de%20Xavana%20Café...`, '_blank')}
                                    style={{ background: THEME.danger, color: 'white', border: 'none', padding: '8px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 'bold', fontSize: 11 }}
                                >
                                    📲 Contactar
                                </button>
                            </div>
                        ))
                    ) : (
                        <div style={{ padding: 15, color: THEME.success, fontWeight: 'bold', textAlign: 'center', background: '#F0FDF4', borderRadius: 10, gridColumn: '1 / -1', fontSize: 14 }}>
                            ✅ Todos los clientes están comprando dentro de su tiempo habitual.
                        </div>
                    )}
                </div>
            </div>

            {/* 3. MATRIZ DE RENTABILIDAD POR PRODUCTO */}
            <div style={{ marginBottom: 30 }}>
                <ChartCard title="Matriz de Valor: Volumen (Kg) vs Ingresos ($)">
                    <div style={{ height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            {/* Busca el componente BarChart dentro de Matriz de Valor y actualiza las barras: */}
                            <BarChart data={data.productos || []} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" fontSize={10} />
                                <YAxis yAxisId="left" orientation="left" stroke={THEME.secondary} fontSize={12} />
                                <YAxis yAxisId="right" orientation="right" stroke={THEME.success} fontSize={12} />
                                <Tooltip 
                                    formatter={(value, name) => {
                                        if (name === "Ingresos ($)") return [`$${value.toLocaleString()}`, name];
                                        return [`${value} Kg`, name];
                                    }}
                                />
                                <Legend />
                                
                                {/* Barra Izquierda: Kilogramos */}
                                <Bar yAxisId="left" dataKey="kg" name="Cantidad (Kg)" fill={THEME.secondary} radius={[4, 4, 0, 0]} />
                                
                                {/* Barra Derecha: AHORA APUNTA A 'dinero' ✅ */}
                                <Bar yAxisId="right" dataKey="dinero" name="Ingresos ($)" fill={THEME.success} radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </ChartCard>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 20, marginBottom: 30 }}>
                {/* 4. DISTRIBUCIÓN PORCENTUAL */}
                <ChartCard title="Distribución de Presentación (%)">
                    <div style={{ height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie 
                                    data={data.distribucion || []} 
                                    innerRadius={70} 
                                    outerRadius={95} 
                                    paddingAngle={5} 
                                    dataKey="value"
                                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                                >
                                    {(data.distribucion || []).map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={index === 0 ? THEME.primary : THEME.warning} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(val) => [`${Number(val).toFixed(1)} Kg`, 'Total']} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </ChartCard>

                {/* 5. TABLA MEJORES CLIENTES */}
                <div style={{ background: 'white', padding: 25, borderRadius: 12, boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                    <h3 style={{ marginTop: 0, marginBottom: 5 }}>🏆 Mejores Clientes (Ranking)</h3>
                    <p style={{fontSize: 12, color: '#6B7280', marginBottom: 20}}>Haz clic para abrir el perfil del cliente.</p>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ textAlign: 'left', borderBottom: `1px solid ${THEME.border}`, color: '#6B7280', fontSize: 11 }}>
                                <th style={{ padding: 12 }}>CLIENTE</th>
                                <th style={{ padding: 12 }}>CICLO</th>
                                <th style={{ padding: 12 }}>INVERSIÓN</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data.topClientes || []).map((c, i) => (
                                <tr key={i} onClick={() => setSelectedDetail(c)} style={{ borderBottom: `1px solid ${THEME.border}`, fontSize: 13, cursor: 'pointer', background: c.enRiesgo ? '#FFF5F5' : 'transparent' }} className="hover-row">
                                    <td style={{ padding: 12 }}>
                                        <div style={{fontWeight: 'bold'}}>{c.nombre}</div>
                                        {c.enRiesgo && <span style={{fontSize: 10, color: THEME.danger, fontWeight: 'bold'}}>⚠️ NECESITA ATENCIÓN</span>}
                                    </td>
                                    <td style={{ padding: 12 }}>{c.frecuencia} d</td>
                                    <td style={{ padding: 12, color: THEME.success, fontWeight: 'bold' }}>
                                        ${Number(c.dinero).toLocaleString('es-MX')}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* MODAL DETALLE CLIENTE (Mantener igual que antes) */}
            {selectedDetail && (
                <div style={modalOverlayStyle}>
                    <div style={modalContentStyle}>
                        <div style={{ background: selectedDetail.enRiesgo ? THEME.danger : THEME.primary, color: 'white', padding: 25, textAlign: 'center' }}>
                            <div style={{ fontSize: 40, marginBottom: 10 }}>{selectedDetail.enRiesgo ? '🚨' : '👤'}</div>
                            <h2 style={{ margin: 0 }}>{selectedDetail.nombre}</h2>
                            <div style={{ opacity: 0.8, fontSize: 13, marginTop: 5 }}>Perfil de Abastecimiento</div>
                        </div>
                        <div style={{ padding: 30 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15, marginBottom: 20 }}>
                                <div style={miniCard}>
                                    <span style={labelStyle}>CICLO PROMEDIO</span>
                                    <div style={{ fontSize: 18, fontWeight: 'bold' }}>{selectedDetail.frecuencia} días</div>
                                </div>
                                <div style={miniCard}>
                                    <span style={labelStyle}>ÚLTIMA COMPRA</span>
                                    <div style={{ fontSize: 18, fontWeight: 'bold', color: selectedDetail.enRiesgo ? THEME.danger : 'inherit' }}>Hace {selectedDetail.diasDesdeUltimo}d</div>
                                </div>
                            </div>
                            <div style={{ background: THEME.bg, padding: 15, borderRadius: 10, marginBottom: 25 }}>
                                <div style={rowStyle}><span>Producto Favorito:</span><strong>{selectedDetail.productoFavorito}</strong></div>
                                <div style={rowStyle}><span>Preferencia:</span><strong>{selectedDetail.preferencia}</strong></div>
                                <div style={{ ...rowStyle, borderTop: `1px solid ${THEME.border}`, marginTop: 10, paddingTop: 10 }}>
                                    <span>Inversión Total:</span>
                                    <strong style={{ color: THEME.success, fontSize: 18 }}>${Number(selectedDetail.dinero).toLocaleString('es-MX')}</strong>
                                </div>
                            </div>
                            {/* Dentro del Modal, después de la inversión total */}
                            <div style={{ marginTop: 20, padding: '15px', background: '#FFFBEB', borderRadius: 10, border: '1px solid #FEF3C7' }}>
                                <div style={{ fontSize: 12, fontWeight: 'bold', color: '#92400E', marginBottom: 10 }}>💡 INSIGHT DE VENTA</div>
                                <div style={{ fontSize: 14, color: '#92400E' }}>
                                    Este cliente suele comprar cada <strong>{selectedDetail.frecuencia} días</strong>. 
                                    {selectedDetail.enRiesgo ? (
                                        <span style={{ display: 'block', marginTop: 5, fontWeight: 'bold' }}>
                                            ⚠️ ¡Lleva {selectedDetail.diasDesdeUltimo} días sin pedir! Sugerimos contacto inmediato.
                                        </span>
                                    ) : (
                                        <span style={{ display: 'block', marginTop: 5 }}>
                                            ✅ Se encuentra dentro de su ciclo normal de reabastecimiento.
                                        </span>
                                    )}
                                </div>
                            </div>
                            <button onClick={() => setSelectedDetail(null)} style={closeBtnStyle}>Cerrar Análisis</button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`.hover-row:hover { background-color: #F9FAFB !important; }`}</style>
        </AdminLayout>
    );
}

// --- COMPONENTES Y ESTILOS ---
const KPICard = ({ title, value, sub, color, icon }) => (
    <div style={{ background: 'white', padding: 25, borderRadius: 12, boxShadow: '0 4px 6px rgba(0,0,0,0.05)', borderTop: `4px solid ${color}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
                <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 'bold', textTransform: 'uppercase' }}>{title}</div>
                <div style={{ fontSize: 24, fontWeight: 'bold', margin: '5px 0' }}>{value}</div>
                <div style={{ fontSize: 12, color: String(sub).includes('-') ? THEME.danger : THEME.success }}>{sub}</div>
            </div>
            <div style={{ fontSize: 24 }}>{icon}</div>
        </div>
    </div>
);

const ChartCard = ({ title, children }) => (
    <div style={{ background: 'white', padding: 25, borderRadius: 12, boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
        <h3 style={{ marginTop: 0, marginBottom: 20, color: THEME.primary, fontSize: 16 }}>{title}</h3>
        {children}
    </div>
);

const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 };
const modalContentStyle = { background: 'white', borderRadius: 16, width: 450, overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' };
const miniCard = { background: '#F9FAFB', padding: 12, borderRadius: 8, border: `1px solid ${THEME.border}`, textAlign: 'center' };
const labelStyle = { fontSize: 11, color: '#6B7280', fontWeight: 'bold', textTransform: 'uppercase' };
const rowStyle = { display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 14 };
const closeBtnStyle = { width: '100%', padding: 12, background: THEME.primary, color: 'white', border: 'none', borderRadius: 8, fontWeight: 'bold', cursor: 'pointer' };

export default AdminAnalisis;